import type { PersonaDto } from '@/lib/api/personas-client';
import { Button, Spinner } from '@monksflow/monks-ui';

import { AudienceStatusChip } from '@/features/audiences';
import { useTranslation } from 'react-i18next';

interface AudienceCreationStateProps {
  persona: PersonaDto;
  onDelete?: () => void;
  isDeleting?: boolean;
}

const containerClasses = 'flex flex-1 flex-col items-center justify-center gap-4 px-8 py-16';

export const AudienceCreationState = ({
  persona,
  onDelete,
  isDeleting,
}: AudienceCreationStateProps) => {
  const { t } = useTranslation('audiences');

  if (persona.creationStatus === 'processing') {
    return (
      <div data-testid="audience-creation-processing" className={containerClasses}>
        <AudienceStatusChip status="processing" />
        <Spinner size="xl" className="size-12" />
        <p className="text-center text-body-md font-medium">
          {t('audienceDetail.audienceCreationState.creatingAudiencePersona')}
        </p>
        <p className="text-center text-body-md text-muted-foreground">
          {t('audienceDetail.audienceCreationState.thisUsuallyTakesAMinute')}
        </p>
      </div>
    );
  }

  if (persona.creationStatus === 'failed') {
    return (
      <div data-testid="audience-creation-failed" className={containerClasses}>
        <AudienceStatusChip status="failed" />
        <p className="text-center text-body-md font-medium">
          {t('audienceDetail.audienceCreationState.audienceCreationFailed')}
        </p>
        <p className="text-center text-body-md text-muted-foreground">
          {persona.creationError ??
            t('audienceDetail.audienceCreationState.somethingWentWrongWhileGenerating')}
        </p>
        {onDelete && (
          <Button variant="outline" onClick={onDelete} disabled={isDeleting}>
            {isDeleting && <Spinner aria-hidden />}
            {t('audienceDetail.audienceCreationState.deleteAndTryAgain')}
          </Button>
        )}
      </div>
    );
  }

  return null;
};
